import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "../lib/assets";
import Reveal from "../components/Reveal";
import MarqueeLink from "../components/MarqueeLink";
const categories = [
  {
    id: "flooring",
    title: "Flooring",
    desc: "Residential and commercial flooring — from timber and laminate to vinyl and carpet.",
    img: IMAGES.flooringDetail,
    items: [
      "Engineered timber flooring",
      "Solid timber flooring",
      "Laminate flooring",
      "LVT (Luxury Vinyl Tile)",
      "Sheet vinyl",
      "Carpet installation",
      "Carpet tiles",
      "Floor preparation and levelling",
    ],
  },
  {
    id: "interior-finishing",
    title: "Interior Finishing",
    desc: "Lining, plastering, painting and wall preparation through to the final finish.",
    img: IMAGES.interiorFinishing,
    items: [
      "GIB fixing",
      "Interior plastering",
      "Interior painting",
      "Wall preparation and finishing",
    ],
  },
  {
    id: "artisan-plastering",
    title: "Artisan Plastering",
    desc: "Architectural plaster finishes for high-end residential and feature commercial spaces.",
    img: IMAGES.plastering,
    items: [
      "Microcement flooring and walls",
      "Venetian plaster",
      "Decorative plaster finishes",
      "Seamless architectural surfaces",
      "Custom designs",
    ],
  },
  {
    id: "tiling",
    title: "Tiling",
    desc: "Precision tiling for wet areas, feature walls and commercial floors.",
    img: IMAGES.tiling,
    items: [
      "Bathroom tiling",
      "Kitchen tiling",
      "Wall tiling",
      "Commercial tiling",
      "Tiles on Jacks / Pedestal",
      "Waterproofing — Interior",
    ],
  },
];

export default function Services() {
  return (
    <div data-testid="services-page" className="bg-[#F5F5F0]">
      {/* HEADER with background image */}
      <section className="relative overflow-hidden -mt-32 md:-mt-36">
        <img
          src="https://customer-assets.emergentagent.com/job_trust-nz-build/artifacts/ie9u5yjf_Trust%20Flooring%202.jpg"
          alt="Architectural interior — Trust Flooring"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/75" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 pt-44 md:pt-56 pb-32 md:pb-44">
          <Reveal>
            <span className="eyebrow text-[#EB1D25]">Services</span>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="mt-10 font-heading text-3xl md:text-5xl lg:text-6xl font-medium text-white tracking-tight max-w-[20ch] leading-[1.1]">
              Services built around the build programme.
            </h1>
          </Reveal>
        </div>
      </section>

      {/* CATEGORIES — clean, sectioned, lots of whitespace */}
      {categories.map((c, idx) => (
        <section
          key={c.id}
          id={c.id}
          data-testid={`services-section-${c.id}`}
          className="py-32 md:py-48 border-t border-[#E2E2DF]"
        >
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-20">
              <div className="md:col-span-7">
                <Reveal>
                  <div className="aspect-[4/5] md:aspect-[3/4] overflow-hidden">
                    <img
                      src={c.img}
                      alt={c.title}
                      className="w-full h-full object-cover transition-transform duration-[1500ms] hover:scale-[1.04]"
                    />
                  </div>
                </Reveal>
              </div>
              <div className="md:col-span-5 md:pt-12">
                <Reveal delay={120}>
                  <span className="eyebrow-muted">
                    {String(idx + 1).padStart(2, "0")} — Service
                  </span>
                  <h2 className="mt-8 font-heading text-2xl md:text-3xl lg:text-4xl text-[#111111] leading-[1.15] font-medium tracking-tight">
                    {c.title}
                  </h2>
                  <p className="mt-6 text-[#545454] text-base md:text-lg leading-relaxed max-w-md">
                    {c.desc}
                  </p>
                </Reveal>

                <Reveal delay={240}>
                  <ul className="mt-12">
                    {c.items.map((item, i) => (
                      <li
                        key={i}
                        className="py-4 border-t border-[#E2E2DF] text-[#111111] font-heading text-sm md:text-base font-normal"
                        data-testid={`service-${c.id}-item-${i}`}
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                </Reveal>

                <Reveal delay={320}>
                  <div className="mt-10">
                    <Link
                      to={`/services/${c.id}`}
                      data-testid={`service-${c.id}-readmore`}
                      className="inline-flex items-center gap-3 font-heading uppercase tracking-[0.22em] text-[11px] font-semibold text-[#EB1D25] border-b border-[#EB1D25] pb-1 hover:text-[#111111] hover:border-[#111111] transition-colors"
                    >
                      Read more <ArrowRight size={12} strokeWidth={1.5} />
                    </Link>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-32 md:py-48 border-t border-[#E2E2DF]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
            <div className="md:col-span-8">
              <Reveal>
                <span className="eyebrow">Get in Touch</span>
                <h2 className="mt-8 font-heading text-2xl md:text-4xl text-[#111111] leading-[1.2] font-medium tracking-tight max-w-2xl">
                  Looking for a trade partner on your next project?
                </h2>
              </Reveal>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Reveal delay={120}>
                <MarqueeLink
                  to="/contact"
                  variant="ghost-dark"
                  data-testid="services-cta-contact"
                >
                  Get in touch
                </MarqueeLink>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
